/**
 * BotD (FingerprintJS open-source, MIT) client-side bot verdict.
 *
 * Design constraints (see change harden-cloak-detection):
 * - **Lazy / dynamic import**: BotD is code-split out of the landing initial
 *   bundle; it loads after first paint, before the verify call.
 * - **monitoring: false**: BotD otherwise fires an AJAX beacon to FingerprintJS.
 *   We never want a third-party beacon on a cloaking landing page (an ad
 *   reviewer could fingerprint it), and we don't need their install stats.
 * - **fail-open**: on load/timeout/throw we return {} — no verdict, no penalty.
 *   The detector's own failure must never block a real visitor.
 */
export interface BotdVerdict {
  bot?: boolean;
  kind?: string;
}

let cached: BotdVerdict | undefined;

export async function getBotdVerdict(timeoutMs = 2500): Promise<BotdVerdict> {
  if (cached) return cached;
  if (typeof window === 'undefined') {
    cached = {};
    return cached;
  }
  try {
    const verdict = await Promise.race([
      (async (): Promise<BotdVerdict> => {
        const { load } = await import('@fingerprintjs/botd');
        const botd = await load({ monitoring: false });
        const result = botd.detect();
        return result.bot ? { bot: true, kind: result.botKind } : { bot: false };
      })(),
      new Promise<BotdVerdict>((resolve) => {
        setTimeout(() => resolve({}), timeoutMs);
      }),
    ]);
    cached = verdict;
  } catch {
    cached = {}; // fail-open
  }
  return cached;
}
